import { useCallback, type RefObject } from 'react';

const DROPDOWN_GAP = 10;
const SMALL_VIEWPORT_WIDTH = 500;
const CENTER_ALIGNMENT_THRESHOLD = 20; // pixels tolerance for center alignment detection

export const useDropdownPositioning = (
  buttonRef: RefObject<HTMLButtonElement>,
  dropdownRef: RefObject<HTMLDivElement>
) => {
  const calculateHorizontalPosition = useCallback(
    (buttonRect: DOMRect, dropdownRect: DOMRect, viewportWidth: number): number => {
      // Very small viewport - check button alignment
      if (viewportWidth < SMALL_VIEWPORT_WIDTH) {
        const buttonCenterX = buttonRect.left + buttonRect.width / 2;
        const viewportCenterX = viewportWidth / 2;

        // Check if button is centered in viewport
        const isButtonCentered =
          Math.abs(buttonCenterX - viewportCenterX) < CENTER_ALIGNMENT_THRESHOLD;

        if (isButtonCentered) {
          // Center dropdown in viewport if button is centered
          return (viewportWidth - dropdownRect.width) / 2;
        } else {
          // Align dropdown with button left edge if button is left-aligned
          return buttonRect.left;
        }
      }

      // Calculate centered position relative to button
      const buttonCenterX = buttonRect.left + buttonRect.width / 2;
      const dropdownHalfWidth = dropdownRect.width / 2;
      let position = buttonCenterX - dropdownHalfWidth;

      // Check for viewport edge overflows
      const overflowsLeft = position <= 0;
      const overflowsRight = position + dropdownRect.width >= viewportWidth;

      if (overflowsLeft) {
        position = buttonRect.left;
      } else if (overflowsRight) {
        position = buttonRect.right - dropdownRect.width;
      }

      return position;
    },
    []
  );

  const calculateDropdownPosition = useCallback(() => {
    // Early exit if refs are not available
    if (!buttonRef.current || !dropdownRef.current) {
      return;
    }

    // Full position calculation
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Calculate positions
    const leftPosition = calculateHorizontalPosition(buttonRect, dropdownRect, viewportWidth);
    const topPosition = buttonRect.bottom + DROPDOWN_GAP;

    // Apply positioning via transform
    dropdownRef.current.style.left = '0';
    dropdownRef.current.style.top = '0';
    dropdownRef.current.style.transform = `translate3d(${Math.round(leftPosition)}px, ${Math.round(topPosition)}px, 0)`;
  }, [calculateHorizontalPosition, buttonRef, dropdownRef]);

  return {
    calculateDropdownPosition,
  };
};
